import * as React from "react";
import styled from "styled-components";
import { FormattedMessage } from "@/i18n/i18n-client";
import type { ControlProps, StylesConfig } from "react-select";
import Filter from "./Filter";
import { SortIcon } from "@/assets/Icons/SortIcon";
import { EmptyCartIcon } from "@/assets/Icons/EmptyCartIcon";
import { FilterIcon } from "@/assets/Icons/FilterIcon";
import { PrimaryButton } from "@/components/Button";
import { EmptyState } from "@/components/UtilityComponents/EmptyState";
import { SortOrder } from "@/generated/graphql";
import { FlexCol, FlexRow, P } from "@/shared/globals";
import LoadingSpinner from "@/shared/globals/UiElements/LoadingSpinner";
import { rtl, themeColor } from "@/shared/styles-utils";
import FilterTags from "../../../default/components/Filter/components/FilterTags";
import { sortOptions } from "../../../default/components/Filter/consts";
import { FilterAndSortProps } from "../../../types";
import { Pagination } from "@/components/Pagination/Pagination";
import { ZIndex } from "@/shared/globals/types";
import {
  usePathname,
  useRouter,
  useSearchParams,
} from "@/i18n/i18n-navigation";
import { parseSearchParams } from "@/shared/utils/search-params";
import {
  LazySelect,
  LazySelectControl,
} from "@/shared/globals/LazyElements/react-select";
import ProductsList from "../../elements/ProductsList";

const MobileFilterSort = ({
  products,
  itemPerPage,
  productsTotalCount,
  offset,
  loading,
  collectionPageId,
  collectionPageTitle,
}: FilterAndSortProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queries = parseSearchParams(searchParams);
  const searchQuery = queries.query || collectionPageTitle;

  const [isOpen, setIsOpen] = React.useState(false);

  const handleChange = (selectedOption: {
    value: { sortBy: string; sortOrder: SortOrder };
  }) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", "1");

    if (selectedOption.value.sortBy) {
      params.set("sortBy", selectedOption.value.sortBy);
    }

    if (selectedOption.value.sortOrder) {
      params.set("sortOrder", selectedOption.value.sortOrder);
    }

    router.push(`${pathname}?${params.toString()}`);
  };

  const sort = {
    sortBy: queries.sortBy,
    sortOrder: queries.sortOrder,
  };

  return (
    <>
      <Filter
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        productsTotalCount={productsTotalCount}
        collectionPageId={collectionPageId}
      />
      <FlexCol alignItems="start">
        {searchQuery && (
          <SearchText>
            <FormattedMessage defaultMessage="Search Results For" id="EK/esq" />{" "}
            {'"' + searchQuery + '"'}{" "}
            <span style={{ opacity: 0.4 }}>
              <FormattedMessage
                defaultMessage="[{resultsCount} Results]"
                id="KkAaz1"
                values={{ resultsCount: productsTotalCount }}
              />
            </span>
          </SearchText>
        )}
        <SortActionWrapper fullWidth>
          <FilterBtn onClick={() => setIsOpen(true)}>
            <FilterIcon />
            <MobileText>
              <FormattedMessage defaultMessage="Filter by" id="S57QRB" />
            </MobileText>
          </FilterBtn>

          <LazySelect
            instanceId="sort-products"
            options={sortOptions}
            onChange={handleChange}
            styles={customStyles}
            defaultValue={sortOptions.find(
              (option) => option.key === `${sort.sortBy}-${sort.sortOrder}`
            )}
            isSearchable={false}
            placeholder={
              <FormattedMessage defaultMessage="Sort by" id="hDI+JM" />
            }
            components={{
              Control,
              IndicatorSeparator: () => null,
              DropdownIndicator: () => null,
            }}
          />
        </SortActionWrapper>
        <FilterTagsWrapper>
          <FilterTags />
        </FilterTagsWrapper>
      </FlexCol>
      {loading ? (
        <LoadingSpinner />
      ) : products?.length === 0 ? (
        <EmptyState
          title={
            queries.filters || queries.minPrice || queries.maxPrice ? (
              <FormattedMessage
                defaultMessage="There are no products match your search criteria"
                id="i0zpgh"
              />
            ) : (
              <FormattedMessage
                defaultMessage="There are no products added yet"
                id="dxKBho"
              />
            )
          }
          image={<EmptyCartIcon />}
        />
      ) : (
        <div style={{ paddingInline: 10 }}>
          <ProductsList products={products} />
          <Pagination
            offset={offset!}
            itemPerPage={itemPerPage!}
            totalCount={productsTotalCount!}
          />
        </div>
      )}
    </>
  );
};

export default MobileFilterSort;

// Functions

const Control = (props: ControlProps) => {
  const { children, ...restProps } = props;
  return (
    <LazySelectControl {...restProps}>
      <SortIcon />
      {children}
    </LazySelectControl>
  );
};

const secondaryColor = "#525252"; // theme.colors.secondary
const customStyles: StylesConfig = {
  container: (provided) => ({
    ...provided,
    width: "50%",
  }),
  control: (provided) => ({
    ...provided,
    border: "none",
    boxShadow: "none",
    justifyContent: "center",
  }),
  valueContainer: (provided) => ({
    ...provided,
    fontWeight: 600,
    fontSize: 14,
    padding: 0,
    flex: "unset",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: secondaryColor,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: secondaryColor,
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "4px",
    boxShadow: "0px 4px 10px 0px rgba(182, 182, 182, 0.25)",
    backgroundColor: "white",
    zIndex: ZIndex.OVERLAY,
  }),
  option: (provided) => ({
    ...provided,
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "1.43",
    color: secondaryColor,
    backgroundColor: "white",
    cursor: "pointer",
    ":hover": {
      backgroundColor: "#F5F5F5",
    },
  }),
};

// Styles

const SearchText = styled(P)`
  font-size: 16px;
  font-weight: 600;
  width: 100%;
  padding: 20px;
  word-break: break-word;
`;

const SortActionWrapper = styled(FlexRow)`
  justify-content: space-around;
  border-top: 1px solid #d8d8d8;
  border-bottom: 1px solid #d8d8d8;
`;

const MobileText = styled(P)`
  font-weight: 600;
  margin-inline-start: 4px;
`;

const FilterBtn = styled(PrimaryButton)`
  background-color: ${themeColor("backgroundReverse")};
  width: 50%;
  border: none;
  ${rtl("border-left", "border-right")}: 1px solid #d8d8d8;
  border-radius: 0;
  padding: 10px 12px;

  &:hover {
    box-shadow: unset;
  }
  &:focus {
    outline: none;
  }
`;

const FilterTagsWrapper = styled.div`
  padding: 20px;
  > div {
    width: 100%;
    margin-left: 0;
    margin-top: 0;
    > * {
      color: ${({ theme }) => theme.text.default};
    }
  }
`;
